import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { X, Eraser, FileText, SkipForward } from "lucide-react";

/*
 * SignatureModal — PDF makbuz indirmeden önce isteğe bağlı dokunmatik imza alır.
 * props:
 *  - onCancel: () => void
 *  - onSubmit: ({ imza_base64, save_to_prim }) => Promise<void>
 *  - existingImza: string | ""  (if prim already has saved imza)
 *  - loading: boolean
 */
export default function SignatureModal({ onCancel, onSubmit, existingImza, loading }) {
  const sigRef = useRef(null);
  const [savePersistent, setSavePersistent] = useState(false);
  const [useExisting, setUseExisting] = useState(!!existingImza);

  const clear = () => {
    sigRef.current?.clear();
  };

  const skip = async () => {
    await onSubmit({ imza_base64: "", save_to_prim: false });
  };

  const confirm = async () => {
    let data = "";
    if (useExisting && existingImza) {
      data = existingImza;
    } else if (sigRef.current && !sigRef.current.isEmpty()) {
      data = sigRef.current.toDataURL("image/png");
    }
    await onSubmit({ imza_base64: data, save_to_prim: savePersistent && !!data });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="signature-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <FileText size={18} /> İmza (İsteğe Bağlı)
          </h3>
          <button onClick={onCancel} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Teslim alan alanına eklenecek imzayı aşağıya çizin. İmza eklemeden PDF indirmek isterseniz <strong>Atla</strong> seçeneğini kullanın.
          </p>

          {existingImza && (
            <label className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
              <input
                data-testid="use-existing-signature"
                type="checkbox"
                checked={useExisting}
                onChange={(e) => setUseExisting(e.target.checked)}
                className="mt-0.5"
              />
              <div className="text-xs text-amber-900 flex-1">
                <strong>Bu prim için kayıtlı imza mevcut.</strong>
                <div className="mt-1 border border-amber-300 bg-white rounded">
                  <img src={existingImza} alt="Kayıtlı imza" className="h-14 mx-auto" />
                </div>
                <div className="mt-1 text-[11px] text-amber-700">İşaretli ise kayıtlı imza kullanılır, aşağıdaki çizim yok sayılır.</div>
              </div>
            </label>
          )}

          <div className={`border-2 border-dashed rounded-lg bg-slate-50 ${useExisting ? "opacity-40 pointer-events-none" : "border-slate-300"}`}>
            <SignatureCanvas
              ref={sigRef}
              penColor="#0F172A"
              backgroundColor="rgba(255,255,255,0)"
              canvasProps={{
                className: "w-full",
                style: { height: 180, touchAction: "none" },
                "data-testid": "signature-canvas",
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={clear}
              disabled={useExisting}
              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-40"
              data-testid="clear-signature"
            >
              <Eraser size={12} /> Temizle
            </button>
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                data-testid="save-signature-persistent"
                type="checkbox"
                checked={savePersistent}
                onChange={(e) => setSavePersistent(e.target.checked)}
              />
              Bu prim için imzayı kaydet
            </label>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={skip}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              data-testid="skip-signature"
            >
              <SkipForward size={14} /> Atla
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              data-testid="confirm-signature"
            >
              <FileText size={14} /> {loading ? "Hazırlanıyor..." : "İmzala ve İndir"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
